import React from 'react';

const Documentation = () => {
  return (
    <section className="ens-tech-manual">
      {/* Gold Card Container */}
      <div style={{
        background: 'var(--black)',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 0 20px var(--gold)',
        maxWidth: '1000px',
        margin: '20px auto',
        fontFamily: 'Helvetica, Arial, sans-serif'
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: 'var(--gold)', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
            BankSystem Technical Manual
          </h1>
          <p style={{ color: 'var(--gold)', fontSize: '16px', marginBottom: '20px' }}>
            Complete Guide to BankSystem Wallet & ENS Identity System
          </p>
        </div>

        {/* Introduction */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            📋 Overview
          </h2>
          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <p>This technical manual provides comprehensive documentation for:</p>
            <ul style={{ paddingLeft: '20px', marginBottom: '15px' }}>
              <li>Minting your .xbsys.eth subdomain through BankSystem's custom contract</li>
              <li>Setting your domain as primary name in BankSystem Identity App</li>
              <li>Using the BankSystem Wallet for asset management</li>
              <li>Understanding the technical architecture</li>
              <li>Troubleshooting common issues</li>
            </ul>
            <p><strong>Prerequisites:</strong> BankSystem Wallet, ETH for gas, basic Web3 knowledge</p>
          </div>
        </div>

        {/* BankSystem Wallet Section */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            🏦 Part 1: BankSystem Wallet
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Overview & Features</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Purpose:</strong> Complete Web3 banking solution with full ENS integration</p>
              <p><strong>Access:</strong> Available to all users (no ENS required for basic features)</p>
              <p><strong>Key Features:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Real-time portfolio tracking with USD values</li>
                <li>ETH and ERC-20 token management</li>
                <li>Transaction history with Etherscan integration</li>
                <li>Secure send/receive functionality</li>
                <li>Multi-token support with auto-detection</li>
                <li>Gas price optimization</li>
                <li>ENS name display and integration</li>
                <li>Local wallet creation with military-grade encryption</li>
                <li>Multi-wallet management</li>
                <li>Seed phrase backup and recovery</li>
                <li>Integrated ENS domain management</li>
              </ul>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Wallet Connection Options</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Multiple Connection Methods:</strong></p>
              <ol style={{ paddingLeft: '20px' }}>
                <li><strong>BankSystem Local Wallet:</strong> Create wallets directly in-app</li>
                <li><strong>Wallet Import:</strong> Import existing wallets via seed phrase</li>
                <li><strong>ENS Integration:</strong> .xbsys.eth domain recognition</li>
              </ol>
              <p><strong>Network Support:</strong> Ethereum Mainnet (additional networks planned)</p>
              <p><strong>Event Handling:</strong> Real-time account and chain change detection</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Local Wallet Security System</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Military-Grade Encryption:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>PBKDF2 Key Derivation:</strong> 100,000 iterations for brute-force protection</li>
                <li><strong>AES-256 Encryption:</strong> Bank-level encryption for all wallet data</li>
                <li><strong>Unique Salt per User:</strong> Each user gets different encryption keys</li>
                <li><strong>Secure Session Management:</strong> Passwords stored only in memory</li>
                <li><strong>Auto-Logout:</strong> Sessions expire after 30 minutes of inactivity</li>
              </ul>
              <p><strong>Security Benefits:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>No static encryption keys - each user has unique encryption</li>
                <li>Private keys never leave the browser unencrypted</li>
                <li>Password-derived keys prevent mass decryption attacks</li>
                <li>Session-based security with automatic cleanup</li>
              </ul>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Getting Started with BankSystem Wallet</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Step 1: Access BankSystem Wallet</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Navigate to BankSystem → Wallet section</li>
                <li>Click "Create BankSystem Wallet" button</li>
                <li>Set a strong password for wallet encryption</li>
              </ul>

              <p><strong>Step 2: Create Your First Wallet</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Click "Generate Secure Wallet"</li>
                <li><strong>CRITICAL:</strong> Write down your seed phrase immediately</li>
                <li>Store seed phrase in multiple secure locations</li>
                <li>Confirm you've saved the seed phrase securely</li>
              </ul>

              <p><strong>Step 3: Fund Your Wallet</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Use the "Receive" function to get your wallet address</li>
                <li>Send ETH or tokens to your new wallet address</li>
                <li>Wait for blockchain confirmation</li>
                <li>Start using your secure BankSystem Wallet</li>
              </ul>

              <div style={{ background: 'rgba(255,68,68,0.1)', border: '1px solid #ff4444', padding: '10px', borderRadius: '5px', marginTop: '10px' }}>
                <p style={{ color: '#ff4444', margin: '0', fontSize: '14px', fontWeight: 'bold' }}>
                  ⚠️ SECURITY WARNING: Your seed phrase is your only backup. BankSystem cannot recover lost seed phrases. Store it securely and never share it with anyone.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Part 2: Minting Process */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            🛠️ Part 2: Minting .xbsys.eth Subdomains
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 1: Access BankSystem Identity App</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Navigation:</strong> BankSystem → Identity section</p>
              <p><strong>Contract Address:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>0x40B4d1d8bCBf4eE89005adF2c48503C53210D26C</code></p>
              <p><strong>Network:</strong> Ethereum Mainnet (Chain ID: 1)</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 2: Connect BankSystem Wallet</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Action:</strong> Click "Connect BankSystem Wallet" button</p>
              <p><strong>Requirements:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>BankSystem Wallet created and unlocked</li>
                <li>Ethereum Mainnet selected</li>
                <li>Sufficient ETH for gas fees</li>
              </ul>
              <p><strong>Gas Estimate:</strong> Varies based on network conditions</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 3: Choose Subdomain</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Naming Rules:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Length:</strong> 3-63 characters</li>
                <li><strong>Characters:</strong> a-z, 0-9, hyphens (-)</li>
                <li><strong>Restrictions:</strong> Cannot start or end with hyphen</li>
                <li><strong>Examples:</strong> "alice", "john-trading", "bob2024"</li>
              </ul>
              <p><strong>Validation:</strong> Real-time checking against smart contract</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 4: Mint Transaction</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Process:</strong></p>
              <ol style={{ paddingLeft: '20px' }}>
                <li>Enter desired subdomain (e.g., "alice")</li>
                <li>Click "Mint Now" button</li>
                <li>Confirm transaction in BankSystem Wallet</li>
                <li>Wait for blockchain confirmation (~15-30 seconds)</li>
                <li>Receive success confirmation</li>
              </ol>
              <p><strong>Contract Method:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>mintSubdomain(string label)</code></p>
              <p><strong>Gas Limit:</strong> Optimized for reliability</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 5: Verification</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Confirmation:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Success alert with transaction hash</li>
                <li>Updated mint count displayed</li>
                <li>Domain immediately available for BankSystem access</li>
              </ul>
              <p><strong>View on Explorer:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>https://etherscan.io/tx/&#123;txHash&#125;</code></p>
            </div>
          </div>
        </div>

        {/* Part 3: Primary Name Setup */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            ⚙️ Part 3: Setting Primary Name in BankSystem Identity App
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 1: Access BankSystem Identity App</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>URL:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>https://banksystem.co/identity/</code></p>
              <p><strong>Network:</strong> Ensure you're on Ethereum Mainnet</p>
              <p><strong>Connection:</strong> Connect same BankSystem Wallet used for minting</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 2: Navigate to Primary Name</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Navigation:</strong></p>
              <ol style={{ paddingLeft: '20px' }}>
                <li>Open BankSystem Identity App dashboard</li>
                <li>Click "🎯 Primary Name" tab</li>
                <li>Select your <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>yourname.xbsys.eth</code> domain</li>
                <li>Click "Set as Primary Name" button</li>
              </ol>
              <p><strong>Note:</strong> Your domain appears immediately after minting</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 3: Set as Primary Name</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Process:</strong></p>
              <ol style={{ paddingLeft: '20px' }}>
                <li>Select your domain from available names</li>
                <li>Click "Set as Primary Name" button</li>
                <li>Confirm the selection in BankSystem Wallet</li>
                <li>Sign the transaction</li>
                <li>Wait for confirmation</li>
              </ol>
              <p><strong>Gas Cost:</strong> Minimal (setting reverse record)</p>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Step 4: Verification</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Confirmation:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>Identity App shows "Primary Name: yourname.xbsys.eth"</li>
                <li>BankSystem services will now recognize this as your identity</li>
                <li>All BankSystem apps automatically detect the primary name</li>
              </ul>
              <p><strong>Benefits:</strong> Human-readable address across BankSystem ecosystem</p>
            </div>
          </div>
        </div>

        {/* Technical Architecture */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            🏗️ Technical Architecture
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>Smart Contract Details</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Contract:</strong> BankSystem ENS Subdomain Registrar</p>
              <p><strong>Root Node:</strong> <code style={{ background: 'var(--black)', padding: '2px 6px', borderRadius: '3px' }}>xbsys.eth</code></p>
              <p><strong>Methods:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><code>mintSubdomain(string label)</code> - Primary minting function</li>
                <li><code>mintFee() → uint256</code> - Returns current minting fee</li>
                <li><code>mintCount(address) → uint256</code> - Tracks mints per wallet</li>
                <li><code>mintedLabels(bytes32) → bool</code> - Checks label availability</li>
              </ul>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>ENS Integration</h3>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Registry:</strong> ENS Mainnet Registry</p>
              <p><strong>Resolver:</strong> Public Resolver for record management</p>
              <p><strong>Subdomain Structure:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Parent:</strong> xbsys.eth (owned by BankSystem)</li>
                <li><strong>Subdomain:</strong> yourname.xbsys.eth (user-owned)</li>
                <li><strong>Control:</strong> Full ownership transferred to minter</li>
              </ul>
            </div>

            <h3 style={{ color: '#00ff88', margin: '20px 0 10px 0' }}>BankSystem Wallet Architecture</h3>
            <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <p><strong>Frontend:</strong> React.js with modern hooks</p>
              <p><strong>Web3 Library:</strong> Ethers.js v6 for blockchain interactions</p>
              <p><strong>Encryption Library:</strong> crypto-js for PBKDF2 and AES-256</p>
              <p><strong>State Management:</strong> React Context API with useState/useEffect</p>
              <p><strong>Security Implementation:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Key Derivation:</strong> PBKDF2 with 100,000 iterations</li>
                <li><strong>Encryption:</strong> AES-256 with unique user salts</li>
                <li><strong>Storage:</strong> Encrypted localStorage with password-derived keys</li>
                <li><strong>Sessions:</strong> Memory-only storage with auto-expiry</li>
                <li><strong>Wallet Generation:</strong> BIP39 compliant seed phrases</li>
              </ul>
              <p><strong>APIs Integrated:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li><strong>Etherscan:</strong> Transaction history & token data</li>
                <li><strong>ENS Public Resolver:</strong> Name resolution</li>
                <li><strong>Real-time Price Feeds:</strong> ETH/USD conversion</li>
                <li><strong>RPC Providers:</strong> Infura/Alchemy for blockchain access</li>
              </ul>
              <p><strong>Security Features:</strong> Input validation, error boundaries, non-custodial design, encrypted storage</p>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            🐛 Troubleshooting Guide
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
              <div style={{ background: 'rgba(255,0,0,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #ff4444' }}>
                <h4 style={{ color: '#ff4444', margin: '0 0 10px 0' }}>❌ Minting Failed</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> Insufficient gas, network issues, or label taken<br/>
                  <strong>Solution:</strong> Check gas limit, ensure Mainnet, try different name
                </p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', border: '1px solid var(--gold)' }}>
                <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>🔗 Domain Not Showing</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> ENS cache delay or wrong network<br/>
                  <strong>Solution:</strong> Wait 2-5 minutes, ensure Mainnet connection
                </p>
              </div>
              <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #00ff88' }}>
                <h4 style={{ color: '#00ff88', margin: '0 0 10px 0' }}>💸 Gas Too High</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> Network congestion<br/>
                  <strong>Solution:</strong> Try during off-peak hours, adjust gas limits
                </p>
              </div>
              <div style={{ background: 'rgba(0,100,255,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #0064ff' }}>
                <h4 style={{ color: '#0064ff', margin: '0 0 10px 0' }}>🔐 Primary Name Error</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> Insufficient permissions or network fee<br/>
                  <strong>Solution:</strong> Ensure wallet control, have ETH for gas
                </p>
              </div>
              <div style={{ background: 'rgba(255,215,0,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid var(--gold)' }}>
                <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>💰 Wallet Connection Issues</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> BankSystem Wallet not unlocked or wrong network<br/>
                  <strong>Solution:</strong> Unlock BankSystem Wallet, ensure Ethereum Mainnet
                </p>
              </div>
              <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #00ff88' }}>
                <h4 style={{ color: '#00ff88', margin: '0 0 10px 0' }}>🔄 Transaction Pending</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> Network congestion or low gas<br/>
                  <strong>Solution:</strong> Wait for confirmation or speed up transaction
                </p>
              </div>
              <div style={{ background: 'rgba(255,68,68,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #ff4444' }}>
                <h4 style={{ color: '#ff4444', margin: '0 0 10px 0' }}>🔒 Wallet Password Issues</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> Wrong password or corrupted data<br/>
                  <strong>Solution:</strong> Use seed phrase to restore wallet, ensure correct password
                </p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', border: '1px solid var(--gold)' }}>
                <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>📱 Session Expired</h4>
                <p style={{ fontSize: '14px', margin: '0', color: 'var(--text-secondary)' }}>
                  <strong>Cause:</strong> 30-minute inactivity timeout<br/>
                  <strong>Solution:</strong> Re-enter password to unlock wallets
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h2 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px' }}>
            💡 Best Practices
          </h2>

          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Name Selection</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Choose memorable, professional names</p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Gas Management</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Mint during low-network activity</p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Security</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Never share private keys or seed phrases</p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Verification</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Always verify transactions on Etherscan</p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Wallet Security</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Use hardware wallets for large amounts</p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '12px', borderRadius: '5px' }}>
                <strong style={{ color: 'var(--gold)' }}>Backup</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Always backup your wallet seed phrase</p>
              </div>
              <div style={{ background: 'rgba(0,255,136,0.1)', padding: '12px', borderRadius: '5px', border: '1px solid #00ff88' }}>
                <strong style={{ color: '#00ff88' }}>Password Strength</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Use strong, unique passwords for wallet encryption</p>
              </div>
              <div style={{ background: 'rgba(0,255,136,0.1)', padding: '12px', borderRadius: '5px', border: '1px solid #00ff88' }}>
                <strong style={{ color: '#00ff88' }}>Multi-Location Backup</strong>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: 'var(--text-secondary)' }}>Store seed phrases in multiple secure locations</p>
              </div>
            </div>

            <div style={{ background: 'rgba(255,68,68,0.1)', border: '1px solid #ff4444', borderRadius: '5px', padding: '15px', marginTop: '20px' }}>
              <h4 style={{ color: '#ff4444', margin: '0 0 10px 0' }}>⚠️ CRITICAL SECURITY REMINDERS</h4>
              <ul style={{ paddingLeft: '20px', margin: '0', color: 'var(--text-secondary)' }}>
                <li><strong>BankSystem cannot recover lost seed phrases</strong> - you are solely responsible for your backups</li>
                <li><strong>Never share your seed phrase with anyone</strong> - including BankSystem support</li>
                <li><strong>Use hardware wallets</strong> for storing significant amounts of cryptocurrency</li>
                <li><strong>Verify all transactions</strong> before confirming in your wallet</li>
                <li><strong>Keep software updated</strong> to protect against security vulnerabilities</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Support Information */}
        <div style={{
          background: 'var(--gold-shadow)',
          border: '1px solid var(--gold)',
          borderRadius: '5px',
          padding: '20px',
          textAlign: 'center',
        }}>
          <h3 style={{ color: 'var(--gold)', margin: '0 0 15px 0' }}>Need Help?</h3>
          <p style={{ color: 'var(--text-light)', margin: '0 0 15px 0', lineHeight: '1.6' }}>
            For technical support or questions about BankSystem services, visit the Support Community
            or MoneyTalks forums for peer assistance and expert guidance.
          </p>
          <div style={{ background: 'rgba(0,255,136,0.1)', border: '1px solid #00ff88', borderRadius: '5px', padding: '12px', marginTop: '15px' }}>
            <p style={{ color: '#00ff88', margin: '0', fontSize: '14px' }}>
              <strong>Remember:</strong> Your .xbsys.eth domain and BankSystem Wallet provide complete access to the entire BankSystem financial ecosystem
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Documentation;
